import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiFilter, FiUser, FiMapPin, FiCalendar, FiClock, FiSearch, FiEdit, FiLock, FiActivity } from 'react-icons/fi';
import { MapContainer, TileLayer, Marker, Circle, Popup, useMap, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { toast } from 'react-toastify';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'leaflet/dist/leaflet.css';

import { PageComponent, DatePeriodFilter, getDateParams, Select, MultiSelect, Button, Card, EditPointModal } from '../../components';
import { ExecutedPointsSkeleton, PointCardSkeleton } from '../../components/Skeletons';
import Api from '../../Api';
import { usePermission } from '../../hooks/usePermission';
import '../CompanyManager/style.css';
import './style.css';

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function MapBounds({ stations, records }) {
    const map = useMap();

    useEffect(() => {
        if (!map) return;

        const bounds = L.latLngBounds();
        let hasPoints = false;

        if (stations && stations.length > 0) {
            stations.forEach(station => {
                if (station.lat && station.lng) {
                    bounds.extend([station.lat, station.lng]);
                    hasPoints = true;
                }
            });
        }

        records.forEach(record => {
            if (record.latitude && record.longitude) {
                bounds.extend([parseFloat(record.latitude), parseFloat(record.longitude)]);
                hasPoints = true;
            }
        });

        if (hasPoints && bounds.isValid()) {
            // Use setTimeout to avoid conflict with map rendering/animations
            const timer = setTimeout(() => {
                if (map && map._container) { // Check if map is still mounted
                     map.fitBounds(bounds, { padding: [50, 50], animate: false });
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [map, stations, records]);

    return null;
}

function PointCardMenu({ onEdit }) {
    const [isOpen, setIsOpen] = useState(false);
    const permissions = usePermission('time_keeping');

    return (
        <div style={{ position: 'relative' }}>
            <button 
                className="menu-dots" 
                onClick={() => setIsOpen(!isOpen)}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
            >
                ⋮
            </button>
            {isOpen && (
                <div className="menu-dropdown">
                    {permissions.canEdit && (
                        <button className="menu-item" onMouseDown={(e) => { e.preventDefault(); onEdit(); }}>
                            <FiEdit size={14} />
                            Editar
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function PointCard({ data, onEdit }) {
    // Extract unique stations from records
    const uniqueStations = useMemo(() => {
        const stations = new Map();
        
        data.records.forEach(record => {
            if (record.station_latitude && record.station_longitude) {
                const key = `${record.station_latitude}_${record.station_longitude}`;
                if (!stations.has(key)) {
                    stations.set(key, {
                        lat: parseFloat(record.station_latitude),
                        lng: parseFloat(record.station_longitude),
                        name: record.station_name,
                        radius: record.fence_radius_km ? record.fence_radius_km * 1000 : 0
                    });
                }
            }
        });
        
        return Array.from(stations.values());
    }, [data.records]);

    const mapCenter = useMemo(() => {
        // Center on first station if available, else first record, else default
        if (uniqueStations.length > 0) {
            return [uniqueStations[0].lat, uniqueStations[0].lng];
        }
        if (data.records && data.records.length > 0 && data.records[0].latitude) {
            return [parseFloat(data.records[0].latitude), parseFloat(data.records[0].longitude)];
        }
        return [-15.793889, -47.882778]; // Brasilia default
    }, [uniqueStations, data.records]);

    const zoom = 15;

    const formatDistance = (value) => {
        if (!value) return '0m';
        const distInKm = parseFloat(value);
        
        // Se for menor que 1km, mostra em metros
        if (distInKm < 1) {
            return `${Math.round(distInKm * 1000)}m`;
        }
        
        // Se for maior ou igual a 1km, mostra em km formatado
        return `${distInKm.toFixed(2).replace('.', ',')}km`;
    };

    // Calculate progress percentage
    const progressPercentage = useMemo(() => {
        let expected = 4; // Default to 4 records (Standard)
        
        if (data.journey) {
             // If journey has NO break times AND is a short shift (<= 6h), expect 2
             const hasBreak = data.journey.break_start_time && data.journey.break_end_time;
             const isShortShift = data.journey.daily_hours && parseFloat(data.journey.daily_hours) <= 6;
             
             if (!hasBreak && isShortShift) {
                expected = 2;
             }
        }
        
        const current = data.records.length;
        const percentage = Math.min((current / expected) * 100, 100);
        return percentage;
    }, [data.journey, data.records.length]);

    return (
        <div className="point-card">
            <div className="point-card-header">
                <div className="header-row">
                    <span className="employee-name">{data.employee_name}</span>
                    <PointCardMenu onEdit={onEdit} />
                </div>
                <div className="header-row meta-info">
                    <span className="icon-text"><FiUser size={14} /> {data.registration_number || '-'}</span>
                    <span className="icon-text"><FiClock size={14} /> {data.records.length} Registros</span>
                </div>
                <div className="header-row meta-info">
                    <span className="icon-text"><FiMapPin size={14} /> {data.company_name || (data.company_id ? `Empresa ${data.company_id}` : '-')}</span>
                </div>
                <div className="header-row position-info">
                    <span className="icon-text suitcase"><FiUser size={14} /> {data.position_name}</span>
                </div>
                <div className="header-row journey-info">
                    <span className="icon-text file"><FiUser size={14} /> {data.journey_name} {data.journey && data.journey.start_time && `- ${data.journey.start_time.substring(0,5)} As ${data.journey.end_time.substring(0,5)}`}</span>
                </div>
                <div className="date-badge">
                <FiCalendar size={14} />
                {data.competence_date ? new Date(data.competence_date + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', year: 'numeric', month: '2-digit', day: '2-digit' }) : '-'}
            </div>
                <div className="progress-bar-container">
                    <div className="progress-bar-fill" style={{ width: `${progressPercentage}%` }}></div>
                </div>
            </div>

            <div className="point-card-swiper">
                {data.records.length > 0 ? (
                    <Swiper
                        modules={[Navigation]}
                        spaceBetween={0}
                        slidesPerView={1}
                        navigation
                        style={{ height: '100%' }}
                    >
                        {data.records.map((record, index) => (
                            <SwiperSlide key={index}>
                                <div className="swiper-slide-content" style={{background: '#f5f5f5', padding: '8px', borderRadius: '8px'}}>
                                    <div className="status-badges">
                                        <span className={`status-badge ${index % 2 === 0 ? 'entry' : 'exit'}`}>
                                            {index % 2 === 0 ? 'Entrada' : 'Saída'}
                                        </span>
                                    </div>
                                    <div className="slide-body">
                                        <div className="slide-photo">
                                            {record.photo ? (
                                                <img src={record.photo} alt={`Ponto ${record.time}`} />
                                            ) : (
                                                <div className="photo-placeholder">
                                                    <FiUser size={40} />
                                                </div>
                                            )}
                                        </div>
                                        <div className="slide-details">
                                            <div className="detail-row"><strong>Registro:</strong> {data.competence_date ? new Date(data.competence_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'} {record.time}</div>
                                            <div className="detail-row"><strong>Local:</strong> {data.worked_stations || record.station_name}</div>
                                            <div className="detail-row"><strong>Distância:</strong> {formatDistance(record.distance)}</div>
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                ) : (
                    <div className="no-records">
                        Nenhum registro
                    </div>
                )}
            </div>

            <div className="point-card-map" style={{zIndex: '1'}}>
                <MapContainer 
                    center={mapCenter} 
                    zoom={zoom} 
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%' }}
                    zoomControl={false}
                    dragging={true}
                    markerZoomAnimation={false}
                    zoomAnimation={false}
                    fadeAnimation={false}
                >
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    <MapBounds stations={uniqueStations} records={data.records} />

                    {/* Station Markers and Fences */}
                    {uniqueStations.map((station, idx) => (
                        <React.Fragment key={`station-${idx}`}>
                            <Marker position={[station.lat, station.lng]}>
                                <Tooltip direction="bottom" offset={[0, 10]} opacity={1} permanent>
                                    {station.name}
                                </Tooltip>
                            </Marker>
                            {station.radius > 0 && (
                                <Circle 
                                    center={[station.lat, station.lng]} 
                                    radius={station.radius} 
                                    pathOptions={{ color: 'var(--primary-color)', fillColor: 'var(--primary-color)', fillOpacity: 0.1 }} 
                                />
                            )}
                        </React.Fragment>
                    ))}

                    {/* Point Markers */}
                    {data.records.map((record, idx) => (
                        record.latitude && record.longitude ? (
                            <Marker 
                                key={idx} 
                                position={[parseFloat(record.latitude), parseFloat(record.longitude)]}
                            >
                                <Tooltip direction="bottom" offset={[0, 10]} opacity={1} permanent>
                                    {record.time}
                                </Tooltip>
                            </Marker>
                        ) : null
                    ))}
                </MapContainer>
            </div>
        </div>
    );
}

// Helper for time comparison
const parseTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return null;
    const [hours, minutes] = timeStr.split(':').map(Number);
    if (isNaN(hours) || isNaN(minutes)) return null;
    return hours * 60 + minutes;
};

// Helper to get day name safely
const getDayName = (dateStr) => {
    if (!dateStr) return null;
    try {
        // Append time to ensure local date parsing if it's YYYY-MM-DD
        const date = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
        const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return dayMap[date.getDay()];
    } catch (e) {
        return null;
    }
};

// Helper for distance calculation (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    if (!lat1 || !lon1 || !lat2 || !lon2) return Infinity;
    
    const R = 6371; // Radius of the earth in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2); 
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
    const d = R * c; // Distance in km
    return d;
};

const deg2rad = (deg) => {
    return deg * (Math.PI/180);
};

export default function ExecutedPoints() {
    const permissions = usePermission('time_keeping');
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [company, setCompany] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companyFilterValues, setCompanyFilterValues] = useState(['all']);
    const [pointsData, setPointsData] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);

    // Filters State
    const [periodMode, setPeriodMode] = useState('daily');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [customPeriod, setCustomPeriod] = useState({ startDate: '', endDate: '' });
    
    const [serviceStations, setServiceStations] = useState([]);
    const [selectedServiceStation, setSelectedServiceStation] = useState('all');

    const [positions, setPositions] = useState([]);
    const [selectedPosition, setSelectedPosition] = useState('all');

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState('all');

    const [selectedPointTypes, setSelectedPointTypes] = useState([]);
    const [outsideFence, setOutsideFence] = useState(false);

    // Edit Modal State
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(null);

    const handleEditPoint = (point) => {
        setSelectedPoint(point);
        setEditModalOpen(true);
    };

    const handleSaveAdjustment = () => {
        fetchPoints();
    };

    const pointTypeOptions = [
        { value: 'odd_records', label: 'Registros Ímpares' },
        { value: 'delay', label: 'Atraso' },
        { value: 'no_return', label: 'Sem Retorno' },
        { value: 'early_entry', label: 'Antecipação de Entrada' },
        { value: 'early_exit', label: 'Antecipação de Saída' },
    ];

    useEffect(() => {
        (async () => {
            if (!permissions.canView) return;
            try {
                const data = await Api.getCompanies();
                const list = Array.isArray(data) ? data : (data.data || []);
                setCompanies(list);
                setCompanyFilterValues(['all']);
            } catch (error) {
                toast.error('Erro ao carregar empresas.');
            }
        })();
    }, []);

    useEffect(() => {
        if (companies.length === 0) return;
        const allValue = 'all';
        const idsWithoutAll = companyFilterValues.filter(v => v !== allValue);
        const selectedIds = companyFilterValues.includes(allValue) ? companies.map(c => String(c.id)) : idsWithoutAll;
        const singleId = selectedIds.length === 1 ? selectedIds[0] : '';
        const selected = companies.find(c => String(c.id) === String(singleId));
        setCompany(selected || null);
        loadFilterOptions(selectedIds);
    }, [companyFilterValues, companies]);

    const loadFilterOptions = async (selectedIds) => {
        try {
            const ids = Array.isArray(selectedIds) && selectedIds.length > 0 ? selectedIds.map(id => Number(id)) : companies.map(c => c.id);
            const [stationsList, positionsList, employeesList] = await Promise.all([
                Promise.all(ids.map(id => Api.getServiceStations(id))),
                Promise.all(ids.map(id => Api.getPositions(id))),
                Promise.all(ids.map(id => Api.getEmployees(id))),
            ]);
            const stationsMerged = stationsList.flat();
            const positionsMerged = positionsList.flat();
            const employeesMerged = employeesList.flat();
            setServiceStations(stationsMerged.map(s => ({ value: s.id, label: s.name })));
            setPositions(positionsMerged.map(p => ({ value: p.id, label: p.name })));
            setEmployees(employeesMerged.map(e => ({ value: e.id, label: e.name })));
        } catch (error) {
            toast.error('Erro ao carregar filtros.');
        }
    };

    const fetchPoints = useCallback(async () => {
        if (!permissions.canView) return;

        setLoading(true);
        setPointsData([]);

        try {
            const dp = getDateParams(periodMode, periodMode === 'custom' ? customPeriod : currentDate);
            const params = {};
            if (dp.startDate) params.startDate = dp.startDate;
            if (dp.endDate) params.endDate = dp.endDate;
            if (selectedServiceStation) params.serviceStationId = selectedServiceStation;
            if (selectedPosition) params.positionId = selectedPosition;
            if (selectedEmployee) params.employeeId = selectedEmployee;

            const allValue = 'all';
            const idsWithoutAll = companyFilterValues.filter(v => v !== allValue);
            const selectedIds = companyFilterValues.includes(allValue) ? companies.map(c => String(c.id)) : idsWithoutAll;
            const ids = selectedIds.length > 0 ? selectedIds.map(id => Number(id)) : companies.map(c => c.id);
            const responses = await Promise.all(ids.map(async (id) => {
                const r = await Api.getExecutedPoints(id, params);
                const arr = (r && r.data) ? r.data : [];
                return arr.map(item => ({ ...item, company_id: item.company_id ?? id }));
            }));
            const merged = responses.flat();
            setPointsData(merged);
            
        } catch (error) {
            console.error('Error fetching points:', error);
            toast.error('Erro ao buscar pontos executados.');
        } finally {
            setLoading(false);
            setInitialLoad(false);
        }
    }, [companyFilterValues, companies, periodMode, currentDate, customPeriod, selectedServiceStation, selectedPosition, selectedEmployee]);

    useEffect(() => {
        fetchPoints();
    }, [fetchPoints]);

    // Client-side Filtering
    const filteredPoints = useMemo(() => {
        if (!pointsData) return [];
        
        // If no filters, return all
        if (selectedPointTypes.length === 0 && !outsideFence) {
            return pointsData;
        }

        const types = selectedPointTypes;

        return pointsData.filter(item => {
            let matches = true;

            // Outside Fence Filter
            if (outsideFence) {
                // Check if any record is outside ALL assigned stations
                const hasOutside = item.records.some(r => {
                    if (!r.latitude || !r.longitude) return false;

                    const lat = parseFloat(r.latitude);
                    const lon = parseFloat(r.longitude);

                    // If no stations assigned, treat as outside (unauthorized location?)
                    if (!item.assigned_stations || item.assigned_stations.length === 0) return true;

                    // Check if inside ANY station
                    const isInsideAny = item.assigned_stations.some(station => {
                        const sLat = parseFloat(station.latitude);
                        const sLon = parseFloat(station.longitude);
                        const radius = parseFloat(station.fence_radius_km);
                        
                        const dist = calculateDistance(lat, lon, sLat, sLon);
                        return dist <= radius;
                    });
                    
                    return !isInsideAny;
                });
                
                if (!hasOutside) matches = false;
            }

            // Point Types Filters
            if (matches && types.length > 0) {
                let typeMatch = false;
                const records = item.records || [];
                const journey = item.journey;

                // 1. Odd Records (Registros Ímpares)
                if (types.includes('odd_records')) {
                    if (records.length % 2 !== 0) typeMatch = true;
                }

                // Journey Rules Based Logic
                if (!typeMatch && journey && item.competence_date) {
                    let start = 0, end = 0, breakStart = 0, breakEnd = 0;
                    let rulesEnabled = true;

                    // Determine expected times
                    if (journey.work_days) {
                        // Weekly Schedule (e.g., 5x2, Partial)
                        const date = new Date(item.competence_date + 'T00:00:00');
                        const dayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
                        const dayName = dayMap[date.getDay()];
                        const rules = journey.work_days[dayName];

                        if (rules && rules.enabled) {
                            start = parseTime(rules.start);
                            end = parseTime(rules.end);
                            breakStart = rules.breakStart ? parseTime(rules.breakStart) : 0;
                            breakEnd = rules.breakEnd ? parseTime(rules.breakEnd) : 0;
                        } else {
                            rulesEnabled = false;
                        }
                    } else {
                        // Fixed/Cyclic Schedule (e.g., 12x36, 6x1, 4x2)
                        // Uses root properties
                        if (journey.start_time && journey.end_time) {
                            start = parseTime(journey.start_time);
                            end = parseTime(journey.end_time);
                            breakStart = journey.break_start_time ? parseTime(journey.break_start_time) : 0;
                            breakEnd = journey.break_end_time ? parseTime(journey.break_end_time) : 0;
                        } else {
                            rulesEnabled = false;
                        }
                    }

                    if (rulesEnabled) {
                        // 2. Delay (Atraso) - > 10 mins tolerance
                        if (types.includes('delay')) {
                            if (records.length > 0) {
                                const entry = parseTime(records[0].time);
                                if (entry > start + 10) typeMatch = true;
                            }
                        }

                        // 3. Early Entry (Antecipação de Entrada)
                        if (!typeMatch && types.includes('early_entry')) {
                            if (records.length > 0) {
                                const entry = parseTime(records[0].time);
                                if (entry < start) typeMatch = true;
                            }
                        }

                        // 4. Early Exit (Antecipação de Saída)
                        if (!typeMatch && types.includes('early_exit')) {
                            if (records.length > 0 && records.length % 2 === 0) {
                                const lastRecord = records[records.length - 1];
                                const exitTime = parseTime(lastRecord.time);
                                
                                // Must be an exit, not a break start
                                // If break defined, ensure it's after breakEnd
                                if (breakEnd > 0) {
                                    if (exitTime > breakEnd && exitTime < end) typeMatch = true;
                                } else {
                                    // No break defined, so even record is exit
                                    if (exitTime < end) typeMatch = true;
                                }
                            }
                        }

                        // 5. No Return (Sem Retorno)
                        if (!typeMatch && types.includes('no_return')) {
                            // Logic: 2 records (Entry + BreakStart) and break is expected
                            // Heuristic: 2nd record is much earlier than End Time (e.g. > 2 hours before)
                            if (records.length === 2 && breakStart > 0) {
                                const lastRecord = records[1];
                                const lastTime = parseTime(lastRecord.time);
                                
                                // If the last punch is closer to Break Start than to End of shift
                                // Or simply if it is significantly before End time
                                if (lastTime < end - 120) { 
                                    typeMatch = true;
                                }
                            }
                        }
                    }
                }

                if (!typeMatch) matches = false;
            }

            return matches;
        });
    }, [pointsData, selectedPointTypes, outsideFence]);

    if (!permissions.canView) {
        return (
            <PageComponent authenticatedPage={true}>
                <div className="container">
                    <div className="manager-header">
                        <div className="manager-header-title">
                            <div className="manager-header-icon">
                                <FiActivity size={22} />
                            </div>
                            <div>
                                <h1>Acesso Negado</h1>
                                <p>Você não tem permissão para visualizar esta página.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </PageComponent>
        );
    }

    if (initialLoad) {
        return (
            <PageComponent authenticatedPage>
                <div className="container">
                    <ExecutedPointsSkeleton />
                </div>
            </PageComponent>
        );
    }

    return (
        <PageComponent authenticatedPage={true}>
            <div className="container">
                <div className="manager-header">
                    <div className="manager-header-title">
                        <div className="manager-header-icon">
                            <FiMapPin size={22} />
                        </div>
                        <div>
                            <h1>Pontos Executados</h1>
                            <p>Visualize os registros de ponto, localizações e fotos dos funcionários.</p>
                        </div>
                    </div>
                </div>

                <Card className="manager-filters-card">
                    <div className="manager-filters-header">
                        <div className="manager-filters-title">
                            <FiFilter size={16} />
                            <span>Filtros</span>
                        </div>
                    </div>
                    
                    <div className="executed-points-filters" style={{ marginBottom: 0 }}>
                        <div className="filter-group filter-group-period">
                            <DatePeriodFilter
                                periodMode={periodMode}
                                setPeriodMode={setPeriodMode}
                                currentDate={currentDate}
                                setCurrentDate={setCurrentDate}
                                customRange={customPeriod}
                                setCustomRange={setCustomPeriod}
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Empresa</label>
                        <MultiSelect
                            id="companyFilter"
                            options={[{ value: 'all', label: 'TODAS' }, ...companies.map(c => ({
                                value: String(c.id),
                                label: c.corporate_name || c.trade_name || c.name || `Empresa ${c.id}`
                            }))]}
                            value={companyFilterValues}
                            onChange={(newValues) => {
                                const allValue = 'all';
                                const currentSelected = companyFilterValues;
                                const isAllSelected = newValues.includes(allValue);
                                const wasAllSelected = currentSelected.includes(allValue);

                                let nextValues;

                                if (isAllSelected && !wasAllSelected) {
                                  const allIds = companies.map(c => String(c.id));
                                  nextValues = [allValue, ...allIds];
                                } else if (!isAllSelected && wasAllSelected) {
                                  nextValues = [];
                                } else if (wasAllSelected && newValues.length < currentSelected.length) {
                                  const remaining = newValues.filter(v => v !== allValue);
                                  nextValues = remaining;
                                } else {
                                  const specificIds = newValues.filter(v => v !== allValue);
                                  const allIds = companies.map(c => String(c.id));
                                  if (specificIds.length === allIds.length && allIds.length > 0) {
                                    nextValues = [allValue, ...specificIds];
                                  } else {
                                    nextValues = specificIds;
                                  }
                                }

                                setCompanyFilterValues(nextValues);
                            }}
                            searchable
                        />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Posto de Serviço</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...serviceStations]}
                                value={selectedServiceStation}
                                onChange={(e) => setSelectedServiceStation(e.target.value)}
                                searchable
                            />
                        </div>
                    </div>

                    <div className="executed-points-filters" style={{ marginTop: '0.75rem' }}>
                        <div className="filter-group">
                            <label className="filter-label">Cargo</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...positions]}
                                value={selectedPosition}
                                onChange={(e) => setSelectedPosition(e.target.value)}
                                searchable
                            />
                        </div>

                        <div className="filter-group">
                            <label className="filter-label">Funcionário</label>
                            <Select
                                placeholder="Todos"
                                options={[{ value: 'all', label: 'TODOS' }, ...employees]}
                                value={selectedEmployee}
                                onChange={(e) => setSelectedEmployee(e.target.value)}
                                searchable
                            />
                        </div>

                        <div className="filter-group" style={{ minWidth: '250px' }}>
                            <label className="filter-label">Apontamentos</label>
                            <Select
                                placeholder="Selecione"
                                searchable
                                multiple
                                options={pointTypeOptions}
                                value={selectedPointTypes}
                                onChange={(e) => setSelectedPointTypes(e.target.value)}
                            />
                        </div>

                        <div className="filter-group" style={{ minWidth: 'auto', flexDirection: 'row', alignItems: 'center', marginTop: 'auto', marginBottom: '0.5rem' }}>
                             <input 
                                type="checkbox" 
                                id="outsideFence" 
                                checked={outsideFence} 
                                onChange={(e) => setOutsideFence(e.target.checked)}
                                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                             />
                             <label htmlFor="outsideFence" style={{ cursor: 'pointer', fontSize: '0.9rem', color: 'var(--text-primary)' }}>
                                 Fora da cerca
                             </label>
                        </div>
                    </div>
                </Card>

                {loading ? (
                    <div className="executed-points-grid" style={{ marginTop: '2rem' }}>
                        {Array.from({ length: 4 }).map((_, i) => (
                            <PointCardSkeleton key={i} />
                        ))}
                    </div>
                ) : (
                    <div className="executed-points-grid" style={{ marginTop: '2rem' }}>
                        {filteredPoints.length > 0 ? (
                            filteredPoints.map((point) => {
                                const companyObj = companies.find(c => String(c.id) === String(point.company_id));
                                const labelFromObj = companyObj
                                  ? (companyObj.corporate_name || companyObj.trade_name || companyObj.name || companyObj.razao_social || companyObj.nome_fantasia || `Empresa ${companyObj.id}`)
                                  : null;
                                const labelFromPoint = point.company_name
                                  || point.company?.trade_name
                                  || point.company?.corporate_name
                                  || point.company?.name
                                  || point.company?.razao_social
                                  || point.company?.nome_fantasia;
                                const companyLabel = labelFromObj || labelFromPoint || (point.company_id ? `Empresa ${point.company_id}` : '-');
                                return (
                                    <PointCard 
                                        key={point.id} 
                                        data={{ ...point, company_name: companyLabel }} 
                                        onEdit={() => handleEditPoint(point)} 
                                    />
                                );
                            })
                        ) : (
                            <div className="no-data-container">
                                <div className="no-data-icon">
                                    <FiSearch size={48} />
                                </div>
                                <h3>Nenhum ponto encontrado</h3>
                                <p>Não encontramos registros para os filtros selecionados.</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <EditPointModal
                isOpen={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                pointData={selectedPoint}
                companyId={selectedPoint?.company_id || company?.id}
                onSave={handleSaveAdjustment}
            />
        </PageComponent>
    );
}
